import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

interface SessionContextType {
    sessionId: string | null;
    isLoading: boolean;
    error: string | null;
    refreshSession: () => Promise<void>;
}

export const SessionContext = createContext<SessionContextType>({
    sessionId: null,
    isLoading: true,
    error: null,
    refreshSession: async () => {},
});

interface SessionProviderProps {
    children: ReactNode;
}

export const SessionProvider: React.FC<SessionProviderProps> = ({ children }) => {
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const createSession = async (): Promise<string | null> => {
        try {
            const response = await fetch('/api/session', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to create session: ${response.status}`);
            }

            const data = await response.json();
            return data.session_id;
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to create session');
            return null;
        }
    };

    const refreshSession = async (): Promise<void> => {
        setIsLoading(true);
        setError(null);

        // Check if we have a session ID in localStorage
        let storedSessionId = localStorage.getItem('rul_labs_session_id');

        if (storedSessionId) {
            // Validate the existing session by trying to get its state
            try {
                const response = await fetch(`/api/session/${storedSessionId}`);
                if (response.ok) {
                    setSessionId(storedSessionId);
                    setIsLoading(false);
                    return;
                } else {
                    localStorage.removeItem('rul_labs_session_id');
                }
            } catch (err) {
                localStorage.removeItem('rul_labs_session_id');
            }
        }

        // Create new session if none exists or stored one is invalid
        const newSessionId = await createSession();
        if (newSessionId) {
            setSessionId(newSessionId);
            localStorage.setItem('rul_labs_session_id', newSessionId);
            localStorage.setItem('session_start_time', new Date().toISOString());
            localStorage.setItem('page_view_count', '0');
            localStorage.setItem('interaction_count', '0');
        } else {
            console.error('Failed to create session, no session ID returned');
        }

        setIsLoading(false);
    };

    useEffect(() => {
        refreshSession();

        // Track session end on page unload
        const handleBeforeUnload = () => {
            if (sessionId) {
                // Use sendBeacon for reliable tracking on page unload
                const payload = {
                    session_id: sessionId,
                    event_type: {
                        type: 'SessionEnded',
                        duration_seconds: Math.floor((Date.now() - Date.parse(localStorage.getItem('session_start_time') || '')) / 1000),
                        page_views: parseInt(localStorage.getItem('page_view_count') || '0'),
                        interactions: parseInt(localStorage.getItem('interaction_count') || '0'),
                    }
                };

                navigator.sendBeacon('/api/events', JSON.stringify(payload));
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []); // Remove sessionId dependency to avoid loops

    // Separate effect for session start time logging
    useEffect(() => {
        if (sessionId && !localStorage.getItem('session_start_time')) {
            localStorage.setItem('session_start_time', new Date().toISOString());
            localStorage.setItem('page_view_count', '0');
            localStorage.setItem('interaction_count', '0');
        }
    }, [sessionId]);

    const value: SessionContextType = {
        sessionId,
        isLoading,
        error,
        refreshSession,
    };

    return (
        <SessionContext.Provider value={value}>
            {children}
        </SessionContext.Provider>
    );
};

export const useSession = () => {
    const context = useContext(SessionContext);
    if (!context) {
        throw new Error('useSession must be used within a SessionProvider');
    }
    return context;
};